import React, { useState, useMemo, useCallback } from 'react';
import { Plus, Home, CheckCircle, Clock, AlertCircle, RefreshCw, X, DollarSign, Filter, Ban } from 'lucide-react';
import { useI18n } from '@/shared/i18n';
import { formatUnitLabel } from '@yawi/domain';
import { useServices, useToast, useDeleteConfirmation, useSection } from '@/app/providers';
import {
  Charge,
  ChargeStatus,
  ChargeType,
  CreateChargeDTO,
  GenerateBatchChargesDTO,
  Unit,
} from '@/shared/types';
import { AsyncSearchableSelect, type SelectOption } from '@/core/ui/components';
import { Button } from '../atoms/Button';
import { Input } from '../atoms/Input';
import { useCrudNotifications, usePaginatedList } from '@/shared/hooks';
import { DataTable, type ColumnDef } from '@/core/ui/components';
import type { QueryParams } from '@yawi/domain';
import { useAbility } from '@/modules/identity/application/ability';

export const AdminChargeList = () => {
  const { t } = useI18n();
  const ability = useAbility();
  const toast = useToast();
  const { chargeRepo, unitRepo } = useServices();
  const { confirmDelete } = useDeleteConfirmation();
  const { withNotification } = useCrudNotifications({
    entityName: 'Cargo',
  });
  const canManageCharge = ability.can('manage', 'all') || ability.can('manage', 'Charge');
  const canCreateCharge = canManageCharge || ability.can('create', 'Charge');
  const canUpdateCharge = canManageCharge || ability.can('update', 'Charge');

  const [statusFilter, setStatusFilter] = useState<string>('ALL');
  const [typeFilter, setTypeFilter] = useState<string>('ALL');
  const [activeResidentCount, setActiveResidentCount] = useState<number | null>(null);

  const {
    data: charges,
    meta,
    loading,
    setPage,
    pageSize,
    setPageSize,
    search,
    setSearch,
    refresh,
    setFilters,
  } = usePaginatedList<Charge, { status?: ChargeStatus; type?: ChargeType }>({
    fetchFn: (params: QueryParams) => chargeRepo.getAllCharges({
      ...params,
      sortOrder: params.sortOrder?.toUpperCase() as 'ASC' | 'DESC' | undefined,
    }),
    initialFilters: {},
    errorTitle: 'Error al cargar cargos',
  });

  // Update filters when status/type changes
  const handleStatusChange = (newStatus: string) => {
    setStatusFilter(newStatus);
    updateFilters(newStatus, typeFilter);
  };

  const handleTypeChange = (newType: string) => {
    setTypeFilter(newType);
    updateFilters(statusFilter, newType);
  };

  /**
   * Ambas claves siempre, con `undefined` cuando es "ALL". `setFilters` del hook hace
   * merge, no reemplazo: omitir la clave dejaba el filtro anterior pegado y volver a
   * "Todos" no limpiaba nada. `buildQueryString` descarta las claves undefined.
   */
  const updateFilters = (status: string, type: string) => {
    setFilters({
      status: status !== 'ALL' ? (status as ChargeStatus) : undefined,
      type: type !== 'ALL' ? (type as ChargeType) : undefined,
    });
  };

  // Batch Generate Modal State
  const [isBatchModalOpen, setIsBatchModalOpen] = useState(false);
  const [generatePeriod, setGeneratePeriod] = useState(() => new Date().toISOString().slice(0, 7));
  const { hasSections } = useSection();
  // Vacío = el backend cobra la cuota de cada sección o la general.
  const [generateAmount, setGenerateAmount] = useState<number | ''>(2500);
  const [generateDueDate, setGenerateDueDate] = useState('');
  const [generateConcept, setGenerateConcept] = useState('');
  const [generating, setGenerating] = useState(false);

  // Fetch unit count when batch modal opens
  React.useEffect(() => {
    if (isBatchModalOpen) {
      if (hasSections) setGenerateAmount('');
      unitRepo.getUnits({ limit: 1, page: 1 })
        .then(response => setActiveResidentCount(response.meta?.total ?? response.data.length))
        .catch(() => setActiveResidentCount(0));
    }
  }, [isBatchModalOpen, unitRepo, hasSections]);

  // Create Charge Modal State
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [createData, setCreateData] = useState<Partial<CreateChargeDTO>>({
    type: ChargeType.MAINTENANCE,
    amount: 0,
    dueDate: new Date().toISOString().split('T')[0],
    period: new Date().toISOString().slice(0, 7),
  });
  const [creating, setCreating] = useState(false);

  const loadUnitOptions = useCallback(async (inputValue: string): Promise<SelectOption[]> => {
    const response = await unitRepo.getUnits({
      page: 1,
      limit: 20,
      search: inputValue || undefined,
      sortBy: 'name',
      sortOrder: 'asc',
    });
    return response.data.map(u => ({
      value: u.id,
      label: formatUnitLabel(u),
    }));
  }, [unitRepo]);

  const getChargeTypeLabel = (type: ChargeType) => {
    const labels: Record<ChargeType, string> = {
      [ChargeType.MAINTENANCE]: t('admin.charges.type.maintenance'),
      [ChargeType.FINE]: t('admin.charges.type.fine'),
      [ChargeType.LATE_FEE]: t('admin.charges.type.lateFee'),
      [ChargeType.EXTRA]: t('admin.charges.type.extra'),
      [ChargeType.SERVICE]: t('admin.charges.type.service'),
      [ChargeType.DEPOSIT]: t('admin.charges.type.deposit'),
      [ChargeType.OTHER]: t('admin.charges.type.other'),
    };
    return labels[type] || type;
  };

  const getChargeStatusLabel = (status: ChargeStatus) => {
    const labels: Record<ChargeStatus, string> = {
      [ChargeStatus.PENDING]: t('admin.charges.status.pending'),
      [ChargeStatus.PARTIAL]: t('admin.charges.status.partial'),
      [ChargeStatus.PAID]: t('admin.charges.status.paid'),
      [ChargeStatus.CANCELLED]: t('admin.charges.status.cancelled'),
    };
    return labels[status] || status;
  };

  const handleGenerateBatch = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canCreateCharge) return;
    if (!generatePeriod || !generateDueDate) return;

    setGenerating(true);
    try {
      let units: Unit[];
      try {
        units = await unitRepo.getAllUnits();
      } catch {
        toast.error('No se pudieron cargar las unidades. Intenta de nuevo.');
        return;
      }

      if (units.length === 0) {
        toast.error('No hay unidades registradas');
        return;
      }

      const data: GenerateBatchChargesDTO = {
        unitIds: units.map(u => u.id),
        period: generatePeriod,
        amount: generateAmount === '' ? undefined : generateAmount,
        dueDate: generateDueDate,
        concept: generateConcept || `Cuota de mantenimiento - ${generatePeriod}`,
      };
      await withNotification(
        () => chargeRepo.generateBatchCharges(data),
        generateAmount === ''
          ? 'Cargos generados con la cuota de cada sección'
          : `Se generaron ${units.length} cargos correctamente`
      );
      setIsBatchModalOpen(false);
      setGeneratePeriod(new Date().toISOString().slice(0, 7));
      setGenerateConcept('');
      refresh();
    } catch {
      // Error ya mostrado por el hook
    } finally {
      setGenerating(false);
    }
  };

  const handleCreateCharge = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canCreateCharge) return;
    if (!createData.unitId || !createData.concept || !createData.amount) return;

    setCreating(true);
    try {
      await withNotification(
        () => chargeRepo.createCharge(createData as CreateChargeDTO),
        'Cargo creado correctamente'
      );
      setIsCreateModalOpen(false);
      setCreateData({
        type: ChargeType.MAINTENANCE,
        amount: 0,
        dueDate: new Date().toISOString().split('T')[0],
        period: new Date().toISOString().slice(0, 7),
      });
      refresh();
    } catch {
      // Error ya mostrado por el hook
    } finally {
      setCreating(false);
    }
  };

  const handleCancelCharge = async (charge: Charge) => {
    if (!canUpdateCharge) return;
    await confirmDelete({
      title: 'Cancelar Cargo',
      itemName: `${charge.concept} - $${charge.amount}`,
      itemType: 'Cargo',
      warningMessage: 'Esta accion no se puede deshacer.',
      onConfirm: async () => {
        await withNotification(
          () => chargeRepo.cancelCharge(charge.id),
          'Cargo cancelado'
        );
        refresh();
      }
    });
  };

  const columns: ColumnDef<Charge>[] = useMemo(() => [
    {
      id: 'member',
      header: 'Unidad',
      cell: (charge) => (
        <div className="flex items-center gap-2">
          <Home size={14} className="text-slate-400"/>
          <span className="font-medium text-slate-800">
            {charge.unitName ?? charge.memberName ?? 'Unidad desconocida'}
          </span>
        </div>
      ),
    },
    {
      id: 'concept',
      header: 'Concepto',
      cell: (charge) => (
        <div>
          <p className="font-bold text-slate-600">{charge.concept}</p>
          <p className="text-xs text-slate-400">{getChargeTypeLabel(charge.type)}</p>
          {charge.period && (
            <p className="text-[10px] text-blue-600 mt-1">Periodo: {charge.period}</p>
          )}
        </div>
      ),
    },
    {
      id: 'amount',
      header: 'Monto',
      className: 'text-right',
      cell: (charge) => (
        <div className="text-right">
          <span className="font-mono text-slate-700">${charge.amount.toLocaleString()}</span>
          {charge.balance !== charge.amount && (
            <p className="text-xs text-amber-600">Saldo: ${charge.balance.toLocaleString()}</p>
          )}
        </div>
      ),
    },
    {
      id: 'dueDate',
      header: 'Vencimiento',
      cell: (charge) => (
        <div>
          <span className="text-slate-500">{charge.dueDate}</span>
          {charge.isOverdue && (
            <p className="text-[10px] text-red-600 font-bold">{charge.daysOverdue} dias vencido</p>
          )}
        </div>
      ),
    },
    {
      id: 'status',
      header: 'Estado',
      cell: (charge) => (
        <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-bold border ${
          charge.status === ChargeStatus.PAID
            ? 'bg-green-50 text-green-700 border-green-200'
            : charge.status === ChargeStatus.PENDING
            ? 'bg-amber-50 text-amber-700 border-amber-200'
            : charge.status === ChargeStatus.PARTIAL
            ? 'bg-blue-50 text-blue-700 border-blue-200'
            : 'bg-slate-50 text-slate-500 border-slate-200'
        }`}>
          {charge.status === ChargeStatus.PAID && <CheckCircle size={12}/>}
          {charge.status === ChargeStatus.PENDING && <Clock size={12}/>}
          {charge.status === ChargeStatus.PARTIAL && <DollarSign size={12}/>}
          {charge.status === ChargeStatus.CANCELLED && <Ban size={12}/>}
          {getChargeStatusLabel(charge.status)}
        </span>
      ),
    },
  ], [t]);

  const filters = (
    <div className="flex items-center gap-3">
      <div className="flex items-center gap-2">
        <Filter size={16} className="text-slate-400" />
        <select
          className="rounded-lg border border-slate-300 text-sm bg-white px-3 py-2"
          value={statusFilter}
          onChange={e => handleStatusChange(e.target.value)}
        >
          <option value="ALL">{t('admin.charges.filter.allStatuses')}</option>
          <option value={ChargeStatus.PENDING}>{t('admin.charges.filter.pending')}</option>
          <option value={ChargeStatus.PARTIAL}>{t('admin.charges.filter.partial')}</option>
          <option value={ChargeStatus.PAID}>{t('admin.charges.filter.paid')}</option>
          <option value={ChargeStatus.CANCELLED}>{t('admin.charges.filter.cancelled')}</option>
        </select>
      </div>
      <select
        className="rounded-lg border border-slate-300 text-sm bg-white px-3 py-2"
        value={typeFilter}
        onChange={e => handleTypeChange(e.target.value)}
      >
        <option value="ALL">{t('admin.charges.filter.allTypes')}</option>
        <option value={ChargeType.MAINTENANCE}>{getChargeTypeLabel(ChargeType.MAINTENANCE)}</option>
        <option value={ChargeType.FINE}>{getChargeTypeLabel(ChargeType.FINE)}</option>
        <option value={ChargeType.LATE_FEE}>{getChargeTypeLabel(ChargeType.LATE_FEE)}</option>
        <option value={ChargeType.EXTRA}>{getChargeTypeLabel(ChargeType.EXTRA)}</option>
        <option value={ChargeType.SERVICE}>{getChargeTypeLabel(ChargeType.SERVICE)}</option>
      </select>
    </div>
  );

  return (
    <>
      <DataTable
        data={charges}
        columns={columns}
        keyExtractor={(charge) => charge.id}
        meta={meta}
        onPageChange={setPage}
        pageSize={pageSize}
        onPageSizeChange={setPageSize}
        title="Cargos"
        searchValue={search}
        onSearchChange={setSearch}
        searchPlaceholder="Buscar por concepto o residente..."
        loading={loading}
        emptyMessage="No se encontraron cargos."
        filters={filters}
        actions={
          canCreateCharge ? (
            <div className="flex gap-2">
              <Button variant="secondary" onClick={() => setIsCreateModalOpen(true)} icon={<Plus size={16}/>}>
                Nuevo Cargo
              </Button>
              <Button onClick={() => setIsBatchModalOpen(true)} icon={<RefreshCw size={16}/>}>
                Generar Cuotas
              </Button>
            </div>
          ) : null
        }
        rowActions={(charge) => charge.status === ChargeStatus.PENDING && canUpdateCharge ? (
          <button
            onClick={() => handleCancelCharge(charge)}
            className="text-xs text-red-600 font-bold hover:underline"
          >
            Cancelar
          </button>
        ) : null}
      />

      {/* Batch Generation Modal */}
      {isBatchModalOpen && canCreateCharge && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/50 backdrop-blur-sm">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-md overflow-hidden animate-in zoom-in-95 duration-200">
            <div className="flex justify-between items-center p-5 border-b border-slate-100">
              <h3 className="font-bold text-slate-800 text-lg">Generar Cuotas Mensuales</h3>
              <button onClick={() => setIsBatchModalOpen(false)}><X size={20} className="text-slate-400"/></button>
            </div>

            <form onSubmit={handleGenerateBatch} className="p-6 space-y-4">
              <div className="bg-blue-50 p-3 rounded-lg text-xs text-blue-700 mb-4">
                Esto generara un cargo PENDIENTE para todos los residentes activos ({activeResidentCount !== null ? activeResidentCount : '...'} usuarios).
              </div>

              <Input
                label="Periodo"
                type="month"
                value={generatePeriod}
                onChange={e => setGeneratePeriod(e.target.value)}
                required
              />

              <Input
                label="Concepto (opcional)"
                value={generateConcept}
                onChange={e => setGenerateConcept(e.target.value)}
                placeholder="Cuota de mantenimiento - Diciembre 2024"
              />

              <div>
                <Input
                  label={hasSections ? 'Monto (opcional)' : 'Monto'}
                  type="number"
                  min={0.01}
                  step="0.01"
                  value={generateAmount}
                  onChange={e => setGenerateAmount(e.target.value === '' ? '' : Number(e.target.value))}
                  required={!hasSections}
                />
                {hasSections && (
                  <p className="mt-1 text-xs text-slate-500">
                    Vacío: se cobra la cuota de cada sección o la general. Con monto, se cobra igual a todas.
                  </p>
                )}
              </div>

              <Input
                label="Fecha de Vencimiento"
                type="date"
                value={generateDueDate}
                onChange={e => setGenerateDueDate(e.target.value)}
                required
              />

              <div className="flex gap-3 pt-4">
                <Button type="button" variant="secondary" className="flex-1" onClick={() => setIsBatchModalOpen(false)}>
                  Cancelar
                </Button>
                <Button type="submit" variant="primary" className="flex-1" icon={<RefreshCw size={16}/>} isLoading={generating}>
                  Generar
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Create Charge Modal */}
      {isCreateModalOpen && canCreateCharge && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/50 backdrop-blur-sm">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-md overflow-hidden animate-in zoom-in-95 duration-200">
            <div className="flex justify-between items-center p-5 border-b border-slate-100">
              <h3 className="font-bold text-slate-800 text-lg">Nuevo Cargo</h3>
              <button onClick={() => setIsCreateModalOpen(false)}><X size={20} className="text-slate-400"/></button>
            </div>

            <form onSubmit={handleCreateCharge} className="p-6 space-y-4">
              <AsyncSearchableSelect
                label="Unidad"
                loadOptions={loadUnitOptions}
                value={createData.unitId || null}
                onChange={(value) => setCreateData({ ...createData, unitId: value ?? '' })}
                placeholder="Buscar unidad..."
                isClearable
                defaultOptions
                minLength={0}
              />

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1.5">Tipo</label>
                <select
                  className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2.5 text-sm"
                  value={createData.type}
                  onChange={e => setCreateData({...createData, type: e.target.value as ChargeType})}
                >
                  <option value={ChargeType.MAINTENANCE}>Mantenimiento</option>
                  <option value={ChargeType.FINE}>Multa</option>
                  <option value={ChargeType.LATE_FEE}>Recargo</option>
                  <option value={ChargeType.EXTRA}>Extra</option>
                  <option value={ChargeType.SERVICE}>Servicio</option>
                  <option value={ChargeType.DEPOSIT}>Deposito</option>
                  <option value={ChargeType.OTHER}>Otro</option>
                </select>
              </div>

              <Input
                label="Concepto"
                value={createData.concept || ''}
                onChange={e => setCreateData({...createData, concept: e.target.value})}
                placeholder="Descripcion del cargo"
                required
              />

              <Input
                label="Periodo"
                value={createData.period || ''}
                onChange={e => setCreateData({...createData, period: e.target.value})}
                placeholder="Ej. 2026-05"
                required
              />

              <Input
                label="Monto"
                type="number"
                value={createData.amount || ''}
                onChange={e => setCreateData({...createData, amount: parseFloat(e.target.value)})}
                required
              />

              <Input
                label="Fecha de Vencimiento"
                type="date"
                value={createData.dueDate || ''}
                onChange={e => setCreateData({...createData, dueDate: e.target.value})}
                required
              />

              <div className="flex gap-3 pt-4">
                <Button type="button" variant="secondary" className="flex-1" onClick={() => setIsCreateModalOpen(false)}>
                  Cancelar
                </Button>
                <Button type="submit" variant="primary" className="flex-1" icon={<Plus size={16}/>} isLoading={creating}>
                  Crear Cargo
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </>
  );
};
